import { PhotoInfoType } from '@dreambooks/core';
import { ServerFile } from '../models';
import { PathService } from '../services/path.service';

type JsonRecord = Record<string, unknown>;

interface PlacedPhotoCollection {
  md5s: Set<string>;
  refsByMd5: Map<string, unknown[]>;
}

const EXCLUDED_PREFIXES = ['clipart_', 'comp_placeholder_'];
const EXCLUDED_TYPES = new Set<number>([
  PhotoInfoType.Clipart,
  PhotoInfoType.ThemeBackground,
  PhotoInfoType.ThemeClipart,
  PhotoInfoType.PhotoDemo
]);

function asRecord(value: unknown): JsonRecord | null {
  return value !== null && typeof value === 'object' ? value as JsonRecord : null;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function asNumber(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function normalizeProjectJson(projectJson: unknown): unknown {
  if (typeof projectJson !== 'string') return projectJson;
  try {
    return JSON.parse(projectJson) as unknown;
  } catch {
    return null;
  }
}

function addMd5(collection: PlacedPhotoCollection, md5: string, ref: unknown): void {
  if (!md5) return;
  collection.md5s.add(md5);
  const refs = collection.refsByMd5.get(md5);
  if (refs) {
    refs.push(ref);
  } else {
    collection.refsByMd5.set(md5, [ref]);
  }
}

function collectPhotoRef(collection: PlacedPhotoCollection, ref: unknown): void {
  if (typeof ref === 'string') {
    addMd5(collection, ref.trim(), ref);
    return;
  }

  const record = asRecord(ref);
  if (!record) return;
  addMd5(collection, asString(record['md5']), ref);
}

function collectElementPhoto(collection: PlacedPhotoCollection, element: unknown): void {
  const record = asRecord(element);
  if (!record) return;

  collectPhotoRef(collection, record['photo']);
  addMd5(collection, asString(record['photoMd5']), element);
  addMd5(collection, asString(record['photoRef']), element);
}

function collectBackgroundPhoto(collection: PlacedPhotoCollection, page: JsonRecord): void {
  const background = asRecord(page['background']);
  const bitmapModel = asRecord(background?.['bitmapModel']);
  collectPhotoRef(collection, bitmapModel?.['photo']);
  collectPhotoRef(collection, background?.['photo']);
}

function collectPage(collection: PlacedPhotoCollection, page: unknown, seen: Set<unknown>): void {
  const record = asRecord(page);
  if (!record || seen.has(record)) return;
  seen.add(record);

  for (const element of asArray(record['elements'])) {
    collectElementPhoto(collection, element);
  }
  for (const element of asArray(record['staticElements'])) {
    collectElementPhoto(collection, element);
  }

  collectBackgroundPhoto(collection, record);

  for (const basePage of asArray(record['pages'])) {
    collectPage(collection, basePage, seen);
  }

  for (const basePage of asArray(record['basePageElements'])) {
    collectPage(collection, basePage, seen);
  }
}

function collectCalendarPhotos(collection: PlacedPhotoCollection, root: unknown): void {
  const record = asRecord(root);
  if (!record) return;

  const eventState = asRecord(record['__calendarEventEditorState'])
    ?? asRecord(record['calendarEventEditorState']);
  for (const entry of asArray(eventState?.['entries'])) {
    const entryRecord = asRecord(entry);
    addMd5(collection, asString(entryRecord?.['photoMd5']), entry);
  }

  for (const cell of asArray(record['calendarCellPhotos'])) {
    const cellRecord = asRecord(cell);
    addMd5(collection, asString(cellRecord?.['photoMd5']), cell);
    collectPhotoRef(collection, cellRecord?.['photo']);
  }
}

function collectPlacedPhotoReferences(projectJson: unknown): PlacedPhotoCollection {
  const root = normalizeProjectJson(projectJson);
  const collection: PlacedPhotoCollection = {
    md5s: new Set<string>(),
    refsByMd5: new Map<string, unknown[]>()
  };

  const record = asRecord(root);
  if (!record) return collection;

  const product = asRecord(record['product']);
  const pages = asArray(product?.['content']).length > 0
    ? asArray(product?.['content'])
    : asArray(record['content']);

  const seen = new Set<unknown>();
  for (const page of pages) {
    collectPage(collection, page, seen);
  }

  collectCalendarPhotos(collection, record);
  collectCalendarPhotos(collection, product);

  return collection;
}

export function collectPlacedPhotoMd5s(projectJson: unknown): Set<string> {
  return collectPlacedPhotoReferences(projectJson).md5s;
}

/** The photo's http(s) storage URL — stored, or PathService-reconstructed; '' when unresolvable. */
function resolvePhotoHttpUrl(record: JsonRecord, pathService: PathService): string {
  const serverName = asString(record['serverName']);
  if (!serverName) return '';

  const url = asString(record['url']);
  if (/^https?:\/\//i.test(url)) return url;

  const reconstructed = pathService.getUrl({
    id: asNumber(record['id']),
    serverId: asNumber(record['serverId']),
    pathStructureId: asNumber(record['pathStructureId']),
    serverName,
    url
  } satisfies ServerFile);

  return /^https?:\/\//i.test(reconstructed) ? reconstructed : '';
}

export function isPhotoStorageResolved(photo: unknown, pathService: PathService): boolean {
  const record = asRecord(photo);
  if (!record) return false;
  return resolvePhotoHttpUrl(record, pathService) !== '';
}

export function findUnresolvedPlacedPhotos(
  projectJson: unknown,
  pathService: PathService
): { md5: string; name: string }[] {
  const root = normalizeProjectJson(projectJson);
  const record = asRecord(root);
  if (!record) return [];

  const collection = collectPlacedPhotoReferences(root);
  const product = asRecord(record['product']);
  const photos = [
    ...asArray(record['photos']),
    ...asArray(product?.['photos'])
  ];
  const photosByMd5 = new Map<string, unknown>();

  for (const photo of photos) {
    const photoRecord = asRecord(photo);
    const md5 = asString(photoRecord?.['md5']);
    if (md5 && !photosByMd5.has(md5)) {
      photosByMd5.set(md5, photo);
    }
  }

  const unresolved: { md5: string; name: string }[] = [];
  const reported = new Set<string>();

  for (const placedMd5 of collection.md5s) {
    if (EXCLUDED_PREFIXES.some(prefix => placedMd5.startsWith(prefix))) continue;

    const placedRefs = collection.refsByMd5.get(placedMd5) ?? [];
    if (placedRefs.some(ref => isExcludedPhoto(ref))) continue;

    let validationMd5 = placedMd5;
    let photo = photosByMd5.get(placedMd5);

    if (placedMd5.startsWith('bg_') && !photo) {
      const underlyingMd5 = placedMd5.substring(3);
      const underlyingPhoto = photosByMd5.get(underlyingMd5);
      if (underlyingPhoto) {
        validationMd5 = underlyingMd5;
        photo = underlyingPhoto;
      }
    }

    if (photo && isExcludedPhoto(photo)) continue;
    if (photo && isPhotoStorageResolved(photo, pathService)) continue;

    const reportMd5 = validationMd5;
    if (reported.has(reportMd5)) continue;

    reported.add(reportMd5);
    unresolved.push({
      md5: reportMd5,
      name: getPhotoName(photo) || getPhotoName(placedRefs[0]) || reportMd5
    });
  }

  return unresolved;
}

function isExcludedPhoto(photo: unknown): boolean {
  const record = asRecord(photo);
  if (!record) return false;

  const md5 = asString(record['md5']);
  if (EXCLUDED_PREFIXES.some(prefix => md5.startsWith(prefix))) return true;

  const type = Number(record['type']);
  return Number.isFinite(type) && EXCLUDED_TYPES.has(type);
}

function getPhotoName(photo: unknown): string {
  const record = asRecord(photo);
  return asString(record?.['name']);
}

// ─── Storage existence probing (cart gate) ───

export interface PlacedPhotoStorageTarget {
  /** md5 to report (the underlying user photo for bg_ backgrounds when the bg_ entry itself is absent). */
  md5: string;
  name: string;
  /** Cheapest storage URL to probe (smallest size when available). */
  probeUrl: string;
  /** For the blocked dialog (data: or http). */
  thumbnail?: string;
}

/** Cheapest URL that proves the photo's storage family exists: `_t`/`_s` size → http thumbnail → any size → original. */
function pickProbeUrl(record: JsonRecord, pathService: PathService): string {
  const sizes = asArray(record['sizes']);
  let firstSizeUrl = '';
  for (const suffix of ['_t.', '_s.']) {
    for (const size of sizes) {
      const sizeUrl = asString(asRecord(size)?.['url']);
      if (!/^https?:\/\//i.test(sizeUrl)) continue;
      if (sizeUrl.includes(suffix)) return sizeUrl;
      if (!firstSizeUrl) firstSizeUrl = sizeUrl;
    }
  }

  const thumbnail = asString(record['thumbnail']);
  if (/^https?:\/\//i.test(thumbnail)) return thumbnail;

  return firstSizeUrl || resolvePhotoHttpUrl(record, pathService);
}

/**
 * Probe targets for every placed photo whose identity IS resolved: the
 * complement of findUnresolvedPlacedPhotos. Identity can be intact while the
 * physical file is gone from storage (storage loss / `_recovering`-marked
 * rows) — these URLs let the cart gate verify actual existence. Includes the
 * `bg_*` direct-file-album entries (customer photos with their own
 * serverName), which the md5-based /photos/check can never cover — their
 * project md5s are synthetic. Same exclusions as the shape check (cliparts,
 * theme assets, demo photos). Deduped by md5 and by URL.
 */
export function collectPlacedPhotoStorageTargets(
  projectJson: unknown,
  pathService: PathService
): PlacedPhotoStorageTarget[] {
  const root = normalizeProjectJson(projectJson);
  const record = asRecord(root);
  if (!record) return [];

  const collection = collectPlacedPhotoReferences(root);
  const product = asRecord(record['product']);
  const photos = [
    ...asArray(record['photos']),
    ...asArray(product?.['photos'])
  ];
  const photosByMd5 = new Map<string, JsonRecord>();
  for (const photo of photos) {
    const photoRecord = asRecord(photo);
    const md5 = asString(photoRecord?.['md5']);
    if (photoRecord && md5 && !photosByMd5.has(md5)) {
      photosByMd5.set(md5, photoRecord);
    }
  }

  const targets: PlacedPhotoStorageTarget[] = [];
  const seenMd5 = new Set<string>();
  const seenUrl = new Set<string>();

  for (const placedMd5 of collection.md5s) {
    if (EXCLUDED_PREFIXES.some(prefix => placedMd5.startsWith(prefix))) continue;

    const placedRefs = collection.refsByMd5.get(placedMd5) ?? [];
    if (placedRefs.some(ref => isExcludedPhoto(ref))) continue;

    let reportMd5 = placedMd5;
    let photo = photosByMd5.get(placedMd5);

    if (placedMd5.startsWith('bg_') && !photo) {
      const underlyingMd5 = placedMd5.substring(3);
      const underlyingPhoto = photosByMd5.get(underlyingMd5);
      if (underlyingPhoto) {
        reportMd5 = underlyingMd5;
        photo = underlyingPhoto;
      }
    }

    if (!photo || isExcludedPhoto(photo)) continue;
    if (seenMd5.has(reportMd5)) continue;
    seenMd5.add(reportMd5);

    const probeUrl = pickProbeUrl(photo, pathService);
    if (!probeUrl || seenUrl.has(probeUrl)) continue;
    seenUrl.add(probeUrl);

    targets.push({
      md5: reportMd5,
      name: getPhotoName(photo) || getPhotoName(placedRefs[0]) || reportMd5,
      probeUrl,
      thumbnail: asString(photo['thumbnail']) || undefined
    });
  }

  return targets;
}
